import { Box, CircularProgress, Stack, useTheme } from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { TabLinks, tabsOffset } from "components/TabLinks";
import {
  CohortIdentifierData,
  getIdentifierKeyAndIdentifierFromCohort,
} from "encamp-shared/src/cohorts/identifierUtilities";
import { CohortNameData, generateCohortName } from "encamp-shared/src/cohorts/nameUtilities";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { CohortStatus } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useEffect, useMemo } from "react";
import { Outlet, useHref, useMatch, useNavigate, useParams } from "react-router-dom";

import { WorkflowComments } from "../../../components/Comments";
import { useBreadcrumb } from "../../../hooks/useBreadcrumbs";
import { kebabToTitleCase } from "../../../util";
import { WorkflowTags } from "../Fulfillment/FulfillmentDetails/Tags";
import { CohortCredentials } from "./CohortCredentials";
import { CohortFulfillmentNotes } from "./CohortFulfillmentNotes";
import { CohortInfo } from "./CohortInfo";
import { CohortMemberSummary } from "./CohortMemberSummary";
import { CohortClosedSummary } from "./Components/CohortClosedSummary";
import { CurrentWorkflow } from "./CurrentWorkflow";
import { Members } from "./Members/index";
import { RelatedCohortsCard } from "./RelatedCohortsCard";
import { useCohortDetails } from "./useCohortDetails";

// Format cohort type for display, handling special cases like LEPC
function formatCohortTypeForDisplay(type: string): string {
  if (type === "LEPC") {
    return "LEPC";
  }
  return kebabToTitleCase(type);
}

export function CohortDetails() {
  const navigate = useNavigate();
  const theme = useTheme();
  const backgroundColor = theme.palette.canvas;

  const { cohortId, activityId } = useParams<{
    cohortId: string;
    activityId?: string;
  }>();
  const { data, loading, refetch } = useCohortDetails();
  const { tenantId } = useTenant();

  const cohortUrl = useHref(cohortId ?? "");

  // Get cohort identifier using the same logic as CohortInfo component
  const identifierInfo = useMemo(() => {
    const cohort = data?.cohort;
    if (!cohort) {
      return {
        identifierKey: "Unknown",
        identifier: "Unknown",
      };
    }

    try {
      const minimalCohort: CohortIdentifierData = {
        id: cohort.id,
        type: cohort.type,
        state: cohort.state,
        fdId: cohort.fdId,
        lepcId: cohort.lepcId,
        credentialUsername: cohort.credentialUsername,
        alternateIdValue: cohort.alternateIdValue,
      };

      return getIdentifierKeyAndIdentifierFromCohort({
        cohort: minimalCohort,
      });
    } catch (error) {
      console.error("Error getting cohort identifier:", error);
      return { identifierKey: "Unknown", identifier: "Unknown" };
    }
  }, [data?.cohort]);

  // Generate cohort name using the shared name utility
  const cohortName = useMemo(() => {
    const cohort = data?.cohort;
    if (!cohort) {
      return "Unknown Cohort";
    }

    try {
      const nameData: CohortNameData = {
        id: cohort.id,
        type: cohort.type,
        state: cohort.state,
        fdName: cohort.fireDepartment?.name,
        lepcName: cohort.lepc?.name,
        credentialUsername: cohort.credentialUsername,
        alternateIdValue: cohort.alternateIdValue,
        members: cohort.members,
      };

      return generateCohortName(nameData);
    } catch (error) {
      console.error("Error generating cohort name:", error);
      return "Unknown Cohort";
    }
  }, [data?.cohort]);

  const cohortTypeString = formatCohortTypeForDisplay(data?.cohort?.type ?? "");
  const reportKindString =
    data?.cohort?.reportKind != null ? prettyPrintEnumValue(data.cohort.reportKind) : "";
  const breadcrumbAddendum =
    reportKindString !== "" ? `${cohortTypeString} – ${reportKindString}` : cohortTypeString;

  // Breadcrumbs (mirror Fulfillment details style)
  useBreadcrumb([
    {
      label: "Fulfillment",
      to: "/staff/fulfillment",
    },
    {
      label: cohortName,
      // Keep last and non-link to mirror Fulfillment details
      // (provider sorts crumbs with undefined `to` to the end)
      to: undefined,
      addendum: breadcrumbAddendum,
    },
  ]);

  const shouldDefaultToActivities = useMatch("/staff/cohorts/:cohortId");

  useEffect(() => {
    if (shouldDefaultToActivities) {
      navigate("activities", { replace: true });
    }
  }, [navigate, shouldDefaultToActivities]);

  const activityTabs = [
    {
      title: "Activities",
      to: activityId ? `activities/${activityId}` : "activities",
    },
    {
      title: "Documents",
      to: "documents",
    },
    {
      title: "Payments",
      to: "payments",
    },
    {
      title: "Workflow Logs",
      to: activityId ? `workflow-logs/${activityId}` : "workflow-logs",
    },
    {
      title: "Jobs",
      to: "jobs",
    },
  ];

  const match = useMatch({
    path: "/staff/cohorts/:cohortId/:tab",
    end: false,
  });

  // Find the matching tab based on the current path
  const currentTab = match?.params.tab;
  const matchingTab = activityTabs.find((tab) => {
    const tabBaseName = tab.to.split("/")[0];
    return tabBaseName === currentTab;
  });

  const selectedActivityTab = matchingTab?.to ?? activityTabs[0].to;

  if (!cohortId) {
    throw new Error("Cohort ID is required");
  }

  return (
    <>
      <TabLinks value={selectedActivityTab} tabs={activityTabs} />
      <Stack
        p={3}
        sx={{
          background: backgroundColor,
          minHeight: {
            xs: `calc(100vh - ${theme.spacing(xsHeight + tabsOffset)})`,
            md: `calc(100vh - ${theme.spacing(mdHeight + tabsOffset)})`,
          },
        }}
      >
        {!data || loading ? (
          <Stack display="flex" alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress />
          </Stack>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
              gap: 2,
            }}
          >
            {/* Main Content Column */}
            <Box>
              <Stack spacing={2}>
                <CohortFulfillmentNotes />
                <Outlet />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <WorkflowComments
                    workflowId={data?.cohort?.currentWorkflow?.id}
                    comments={data?.cohort?.currentWorkflow?.comments}
                    onRefetch={refetch}
                    buttonText="Submit"
                    title="Workflow Comments"
                    loading={loading}
                  />
                  <WorkflowTags
                    workflowId={data?.cohort?.currentWorkflow?.id}
                    workflowTags={data?.cohort?.currentWorkflow?.tags}
                    title="Workflow Tags"
                    localStorageKey="cohort-workflow-recent-tags"
                    commonTags={["batch-processing", "compliance", "review", "cohort"]}
                    loading={loading}
                  />
                </Box>

                {/* Cohort Summary - only shown for CLOSED cohorts */}
                {data?.cohort?.status === CohortStatus.Closed && <CohortClosedSummary />}

                <Members
                  reportingYear={data?.cohort?.reportingYear}
                  isClosed={data?.cohort?.status === CohortStatus.Closed}
                />
              </Stack>
            </Box>

            <Box>
              <Stack spacing={2}>
                <CohortCredentials />
                <RelatedCohortsCard />
                <CohortInfo />
                <CurrentWorkflow />
                <CohortMemberSummary />
              </Stack>
            </Box>
          </Box>
        )}
      </Stack>
    </>
  );
}
